import { Dialog, NumberField, setWeek, type LeavePerson } from '@trippath/shared';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dayRange } from '../../lib/countryFacts';
import { DayKindIcon } from '../../components/icons/DayKindIcon';
import { runsOf } from './leave';

interface Props {
  trip: string;
  person: LeavePerson;
  onClose: () => void;
  onSaved: (people: LeavePerson[]) => void;
}

/** Monday to Sunday, 5 January 2026 a Monday. */
const WEEK = Array.from({ length: 7 }, (_, at) => Date.UTC(2026, 0, 5 + at));

/**
 * A person's working week and days of leave a year, what the trip takes of them, and the days
 * they marked: on leave, sick, working while away.
 */
export function LeaveDialog({ trip, person, onClose, onSaved }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const locale = lang === 'et' ? 'et-EE' : 'en-GB';
  const id = useId();
  const [workdays, setWorkdays] = useState(person.workdays);
  const [perYear, setPerYear] = useState<number | null>(person.leavePerYear);
  const [failed, setFailed] = useState(false);
  const short = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const long = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });

  const save = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    // Nothing sent while the leave a year does not read; the field says why.
    if (perYear === null) return;
    setFailed(false);
    setWeek(trip, person.person, workdays, perYear).then(onSaved, () => {
      setFailed(true);
    });
  };

  return (
    <Dialog open title={t('leave.title', { name: person.name })} onClose={onClose}>
      <form className="form" onSubmit={save} noValidate>
        <div className="field">
          <span className="field-label" id={`${id}-week`}>
            {t('leave.week')}
          </span>
          <div className="leave-week" role="group" aria-labelledby={`${id}-week`}>
            {WEEK.map((day, at) => {
              const on = workdays.charAt(at) === '1';
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={on}
                  aria-label={long.format(day)}
                  onClick={() => {
                    setWorkdays(
                      `${workdays.slice(0, at)}${on ? '0' : '1'}${workdays.slice(at + 1)}`,
                    );
                  }}
                >
                  {short.format(day)}
                </button>
              );
            })}
          </div>
          <p className="quiet field-hint">{t('leave.weekHint')}</p>
        </div>
        <NumberField
          label={t('leave.perYear')}
          value={perYear}
          min={0}
          max={366}
          onChange={setPerYear}
        />
        {/* What the trip takes, as the server counted it with the week kept. */}
        <div className="leave-used">
          <span>{t('leave.used')}</span>
          <strong className="mono">
            {t('leave.usedOf', { used: person.used, perYear: person.leavePerYear })}
          </strong>
          <span className="leave-meter" aria-hidden="true">
            <span
              style={{
                width: `${String(Math.min(100, (person.used / Math.max(1, person.leavePerYear)) * 100))}%`,
              }}
            />
          </span>
        </div>
        <div className="field">
          <span className="field-label">{t('leave.marked')}</span>
          {person.days.length === 0 ? (
            <p className="quiet">{t('leave.none')}</p>
          ) : (
            <ul className="leave-runs">
              {runsOf(person.days).map((run) => (
                <li key={run.from}>
                  <span className={`day-mark mark-${run.kind}`}>
                    <DayKindIcon kind={run.kind} />
                    {t(`leave.kind.${run.kind}`)}
                  </span>
                  <span className="mono">{dayRange(run.from, run.to, lang)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {failed && (
          <p className="field-error" role="alert">
            {t('plan.failed')}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="button" onClick={onClose}>
            {t('dialog.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {t('leave.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
